import { getLibraryProgressFn } from "@/actions/community/handler";
import {
  getLibraryEntryOptions,
  getLibraryPathOptions,
} from "@/actions/library/handler";
import { Editor } from "@/components/blocks/editor-essay/editor";
import { ContentViewTracker } from "@/components/content/content-view-tracker";
import { ContextualContentCta } from "@/components/content/contextual-content-cta";
import { LibraryProgressButton } from "@/components/library/library-progress-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  libraryDifficultyLabels,
  libraryRightsLabels,
  libraryStudyTypeLabels,
} from "@/utils/library-catalog";
import { seo } from "@/utils/seo";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  notFound,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  MessageSquare,
} from "lucide-react";

export const Route = createFileRoute("/_app/kutuphane/kaynak/$entrySlug")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      getLibraryEntryOptions(params.entrySlug),
    );
    if (!data.entry) throw notFound();
    return data.entry;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const canonical = `https://atalaythecoach.com/kutuphane/kaynak/${loaderData.slug}`;
    return {
      meta: seo({
        title: `${loaderData.turkishTitle} | Bilim Kütüphanesi`,
        description: loaderData.summary.slice(0, 160),
        canonical,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.turkishTitle,
            description: loaderData.summary,
            datePublished: loaderData.publishedAt?.toISOString(),
            dateModified: loaderData.updatedAt?.toISOString(),
            mainEntityOfPage: canonical,
            citation: loaderData.doi
              ? `https://doi.org/${loaderData.doi}`
              : loaderData.sourceUrl,
            author: { "@type": "Person", name: "Alican Atalay" },
          }),
        },
      ],
    };
  },
  component: LibraryEntryPage,
});

function LibraryEntryPage() {
  const entry = Route.useLoaderData();
  const { user } = useRouteContext({ from: "/_app" });
  const progress = useQuery({
    queryKey: ["library-progress"],
    queryFn: () => getLibraryProgressFn(),
    enabled: Boolean(user),
  });
  const primaryPath = entry.paths[0];
  const pathQuery = useQuery({
    ...getLibraryPathOptions(primaryPath?.slug || ""),
    enabled: Boolean(primaryPath),
  });
  const pathEntries = pathQuery.data?.path?.entries || [];
  const index = pathEntries.findIndex((item) => item.entryId === entry.entryId);
  const previous = index > 0 ? pathEntries[index - 1] : null;
  const next = index >= 0 ? pathEntries[index + 1] : null;
  const completed =
    progress.data?.completedEntryIds.includes(entry.entryId) || false;

  return (
    <main className="min-h-screen bg-background pt-28 pb-20 text-foreground">
      <ContentViewTracker
        resourceType="library_entry"
        resourceId={entry.entryId}
      />
      <article className="container mx-auto max-w-4xl px-4">
        <Link
          to={primaryPath ? "/kutuphane/$pathSlug" : "/kutuphane"}
          params={primaryPath ? { pathSlug: primaryPath.slug } : undefined}
          className="inline-flex items-center text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {primaryPath ? primaryPath.title : "Kütüphane"}
        </Link>

        <header className="mt-8">
          <div className="flex flex-wrap gap-2">
            <Badge>{entry.publicationDate.slice(0, 4)}</Badge>
            <Badge variant="outline">
              {libraryStudyTypeLabels[entry.studyType]}
            </Badge>
            <Badge variant="outline">
              {libraryDifficultyLabels[entry.difficulty]}
            </Badge>
            {entry.topics.map((topic) => (
              <Badge key={topic} variant="secondary">
                {topic}
              </Badge>
            ))}
          </div>
          <h1 className="mt-5 font-extrabold text-3xl tracking-tight sm:text-5xl">
            {entry.turkishTitle}
          </h1>
          <p className="mt-4 text-muted-foreground text-xl leading-8">
            {entry.originalTitle}
          </p>
          <div className="mt-5 text-muted-foreground text-sm">
            <p>{entry.authors.join(", ")}</p>
            <p className="mt-1">
              {[entry.journal, entry.publicationDate]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <LibraryProgressButton
              entryId={entry.entryId}
              initialCompleted={completed}
              authenticated={Boolean(user)}
            />
            <Button asChild variant="outline">
              <a
                href={entry.openAccessUrl || entry.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.openAccessUrl
                  ? "Açık erişim kaynağını aç"
                  : "Orijinal kaynağı aç"}{" "}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </header>

        <div className="mt-12 grid gap-5">
          <Insight title="Bu çalışma ne söylüyor?" text={entry.findings} />
          <Insight
            title="Ne söylemiyor ve sınırlılıkları neler?"
            text={entry.limitations}
          />
          <Insight
            title="Sporcu ve koç için pratik karşılığı"
            text={entry.practicalTakeaways}
            emphasis
          />
        </div>

        <section className="mt-12">
          <h2 className="font-bold text-2xl">Çalışmayı bağlamıyla oku</h2>
          <p className="mt-3 text-muted-foreground leading-7">
            {entry.summary}
          </p>
          {entry.editorialContent && (
            <div className="prose prose-neutral dark:prose-invert mt-8 max-w-none">
              <Editor
                editorSerializedState={entry.editorialContent as never}
                readOnly
              />
            </div>
          )}
        </section>

        {entry.coachCommentary && (
          <section className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
            <Badge>Koç yorumu</Badge>
            <p className="mt-4 leading-7">{entry.coachCommentary}</p>
          </section>
        )}

        <section className="mt-8 rounded-xl border bg-muted/20 p-4 text-muted-foreground text-sm">
          <strong className="text-foreground">Kaynak kullanımı:</strong>{" "}
          {libraryRightsLabels[entry.rightsStatus]}. Bu sayfa çalışmanın tam
          metninin yerine geçmez; değerlendirme özgün editoryal içeriktir.
          {entry.licenseUrl && (
            <>
              {" "}
              <a
                className="underline"
                href={entry.licenseUrl}
                target="_blank"
                rel="noreferrer"
              >
                Lisansı görüntüle
              </a>
              .
            </>
          )}
        </section>

        <div className="mt-10">
          <ContextualContentCta
            href={entry.ctaHref}
            label={entry.ctaLabel}
            source="library"
          />
        </div>

        {entry.questions.length > 0 && (
          <section className="mt-14 border-t pt-8">
            <h2 className="flex items-center font-bold text-2xl">
              <MessageSquare className="mr-2 h-5 w-5" />
              İlgili uzman soruları
            </h2>
            <div className="mt-5 grid gap-3">
              {entry.questions.map((question) => (
                <Link
                  key={question.tagId}
                  to="/forum/$tagId"
                  params={{ tagId: question.slug || question.tagId }}
                  className="rounded-xl border p-4 font-semibold hover:border-primary"
                >
                  {question.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        <nav className="mt-14 grid gap-4 border-t pt-8 sm:grid-cols-2">
          {previous ? (
            <Link
              to="/kutuphane/kaynak/$entrySlug"
              params={{ entrySlug: previous.slug }}
              className="rounded-xl border p-4"
            >
              <span className="text-muted-foreground text-xs">
                Önceki çalışma
              </span>
              <strong className="mt-1 flex items-center">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {previous.turkishTitle}
              </strong>
            </Link>
          ) : (
            <div />
          )}
          {next && (
            <Link
              to="/kutuphane/kaynak/$entrySlug"
              params={{ entrySlug: next.slug }}
              className="rounded-xl border p-4 text-right"
            >
              <span className="text-muted-foreground text-xs">
                Sıradaki çalışma
              </span>
              <strong className="mt-1 flex items-center justify-end">
                {next.turkishTitle}
                <ArrowRight className="ml-2 h-4 w-4" />
              </strong>
            </Link>
          )}
        </nav>
      </article>
    </main>
  );
}

function Insight({
  title,
  text,
  emphasis = false,
}: { title: string; text: string; emphasis?: boolean }) {
  return (
    <Card className={emphasis ? "border-primary/40 bg-primary/5" : undefined}>
      <CardHeader>
        <CardTitle className="text-xl">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground leading-7">{text}</p>
      </CardContent>
    </Card>
  );
}
